import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * FinishDialog — "İş Tamamlandı" akışı
 * Props:
 *   open, onOpenChange
 *   projectId, itemId, itemName
 *   stages: string[]  (settings.manufacturing_stages)
 *   onFinished: (result) => void  // called on success
 */
export default function FinishDialog({ open, onOpenChange, projectId, itemId, itemName, stages = [], onFinished }) {
  const [mode, setMode] = useState(null); // "done" | "next"
  const [nextStage, setNextStage] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setMode(null); setNextStage(""); };
  const handle = async (v) => {
    onOpenChange(v);
    if (!v) reset();
  };

  const submit = async () => {
    if (!mode) return;
    if (mode === "next" && !nextStage) { toast.error("Yeni aşamayı seçin"); return; }
    setBusy(true);
    try {
      const body = mode === "next" ? { next_stage: nextStage } : {};
      const r = await api.post(`/projects/${projectId}/items/${itemId}/finish`, body);
      toast.success(mode === "next" ? `Yeni aşama başlatıldı: ${nextStage}` : "İşiniz tamamlandı");
      handle(false);
      onFinished?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handle}>
      <DialogContent data-testid="finish-dialog">
        <DialogHeader>
          <DialogTitle>İş Tamamlandı</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-zinc-800">{itemName}</span> — hangi seçenek?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="finish-done-btn"
              onClick={() => setMode("done")}
              className={`p-4 rounded-md border text-left transition ${mode === "done" ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-300"}`}
            >
              <CheckCircle2 size={18} className="text-emerald-600 mb-2" />
              <div className="font-medium">Ürünle işim bitti</div>
              <div className="text-xs text-zinc-500 mt-1">Devraldığınız aşamayı tamamladınız ve bu ürünle işiniz bitti.</div>
            </button>
            <button
              type="button"
              data-testid="finish-next-btn"
              onClick={() => setMode("next")}
              className={`p-4 rounded-md border text-left transition ${mode === "next" ? "border-blue-500 bg-blue-50" : "border-zinc-200 hover:border-zinc-300"}`}
            >
              <ArrowRight size={18} className="text-blue-600 mb-2" />
              <div className="font-medium">Yeni aşamaya başla</div>
              <div className="text-xs text-zinc-500 mt-1">Şu anki aşamayı bitirip aynı ürünün başka bir aşamasına geçin.</div>
            </button>
          </div>
          {mode === "next" && (
            <div>
              <Label>Yeni Aşama</Label>
              <Select value={nextStage} onValueChange={setNextStage}>
                <SelectTrigger data-testid="finish-next-stage-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {stages.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => handle(false)}>İptal</Button>
          <Button
            data-testid="finish-confirm"
            onClick={submit}
            disabled={!mode || busy}
            className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40"
          >Onayla</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
